<template>
  <div>
    <h1>Live on The Water</h1>
    <b-row>
      <b-col cols="12">
        <filters @search="applyFilter" :apiRequestData="apiRequestData"/>
      </b-col>
    </b-row>

    <b-row>
      <b-col cols="6">
        <b-card no-body>
          <vessels-map
            class="dashboard-map"
            :filter-values="apiFilterValues"
            :vessels-count="vesselsCount"
            @map-update="parameters => {
              // uncomment when the map boundingBox will be required to filter all requests
              // applyFilter({ boundingBox: parameters })
            }"
          />
        </b-card>
      </b-col>
      <b-col cols="3">
        <directions-stat
          title="Top Origins"
          class="dashboard-card"
          top-section-name="country"
          bottom-section-name="port"
          :stats="directionLoadStats"/>
      </b-col>
      <b-col cols="3">
        <directions-stat
          title="Top Destinations"
          class="dashboard-card"
          top-section-name="country"
          bottom-section-name="port"
          :stats="directionDischargeStats"/>
      </b-col>
    </b-row>

    <b-row>
      <b-col cols="6">
        <periodic-stats
          title="Timeseries"
          class="dashboard-card"
          :seasonal="true"
          :grouped="true"
          :stacked="true"
          :data-labels="true"
          :stats="periodicStat.timeseries.data"
          :range-choices="periodicStat.timeseries.range.choices"
          @search="
            parameters => {
              applyFilter({
                _periodicStatSection: 'timeseries',
                _periodicStatRange: parameters.range,
                _periodicStatGroup: parameters.group
              });
            }"
          :apiRequestIsPerforming="false"
          :export-types="null"
          @export="format => handleExportTimeseries(format)"/>
      </b-col>
      <b-col cols="3">
        <segments-chart
          class="dashboard-card"
          title="Movement Status"
          :items="movementStats"
        />
      </b-col>
      <b-col cols="3">
        <segments-chart
          class="dashboard-card"
          title="Vessel Classes"
          :items="vesselsStats"
        />
      </b-col>
    </b-row>
  </div>
</template>

<script>
import getNested from 'lodash-es/get'
import { mapActions } from 'vuex'

import { regions } from '@/shared/constants/regions'
import Filters from '../components/livestats/Filters'
import DirectionsStat from '../components/livestats/DirectionsStat'
import PeriodicStats from '../components/tradeflows/PeriodicStats'
import SegmentsChart from '../components/widgets/SegmentsChart'
import VesselsMap from '../components/livestats/VesselsMap'

export default {
  name: 'live-stats',

  components: {
    VesselsMap,
    SegmentsChart,
    PeriodicStats,
    DirectionsStat,
    Filters,
  },

  data() {
    return {
      apiRequestData: {},
      apiFilterValues: {},

      exportTypes: ['csv'],

      directionStats: {
        regions: {
          load: [],
          discharge: [],
          method: 'getTopRegions',
          apiFieldName: 'regionVolumes',
          titleFieldName: 'region',
          titleConverter: {
            sourceFieldName: 'regionId',
            destinationFieldName: 'regionName',
            table: regions,
          },
        },
        countries: {
          load: [],
          discharge: [],
          method: 'getTopCountries',
          apiFieldName: 'countryVolumes',
          titleFieldName: 'country',
        },
        ports: {
          load: [],
          discharge: [],
          method: 'getTopPorts',
          apiFieldName: 'portVolumes',
          titleFieldName: 'port',
        },
      },

      movementStats: [],
      vesselsStats: [],

      periodicStat: {
        timeseries: {
          data: [],
          range: {
            value: 'weekly',
            choices: [
              {text: 'Weekly', value: 'weekly'},
              {text: 'Monthly', value: 'monthly'},
              {text: 'Quarterly', value: 'quarter'},
              {text: 'Annual', value: 'year'},
            ],
          },
          group: {
            value: null,
            choices: [],
          },
          method: 'getTimeseries',
        },
      },
      vesselsCount: 0,
    };
  },

  methods: {
    ...mapActions('api/livestates',
      [
        'getLadenState',
        'getTopRegions',
        'getTopCountries',
        'getTopPorts',
        'getVoyageState',
        'getVesselSegments',
        'getVesselsCount',
        'getTimeseries',
      ]
    ),

    async applyFilter(apiFilterValues) {
      this.apiFilterValues = {
        ...this.apiFilterValues,
        ...this.getBaseFilterParameters(apiFilterValues),
      };
      // console.log('applyFilter, origin parameters', JSON.stringify(apiFilterValues, null, 2));
      // console.log('applyFilter, converted parameters', JSON.stringify(this.apiFilterValues, null, 2));

      const periodicSectionName = apiFilterValues._periodicStatSection;
      if(periodicSectionName) {
        // only periodic dashboards
        await this.loadPeriodicStats(apiFilterValues)
      } else {
        // all dashboards
        const baseParameters = this.getBaseFilterParameters();
        await Promise.all([
          this.loadRegions(baseParameters),
          this.loadVoyageState(baseParameters),
          this.loadVesselSegments(baseParameters),
          this.loadVesselsCount(baseParameters),
          this.loadPeriodicStats(baseParameters),
        ]);
      }
    },

    async loadRegions(apiFilterValues) {
      const directions = ['load', 'discharge'];

      const promises = Object.keys(this.directionStats).map(sectionName => {
        const section = this.directionStats[sectionName];
        const method = this[section.method];

        return Promise.all(
          directions.map(async direction => {
            const response = await method({...apiFilterValues, group: direction});

            this.directionStats[sectionName][direction] = getNested(
              response, section.apiFieldName, []
            ).map(item => {
              let title = getNested(item, section.titleFieldName, 'unknown');
              title = this.convertRegionTitle(section, title);

              return {
                title,
                volume: getNested(item, 'volume', 0),
              };
            });
          }));
      });

      await Promise.all(promises);
    },

    convertRegionTitle(section, title) {
      if(section.titleConverter) {
        const sourceField = section.titleConverter.sourceFieldName;
        const destinationField = section.titleConverter.destinationFieldName;

        const tableItem = section.titleConverter.table.find(
          item => item[sourceField] === title
        );
        return getNested(tableItem, destinationField, 'unknown');
      } else {
        return title;
      }
    },

    async loadVoyageState(apiFilterValues) {
      const response = await this.getVoyageState(apiFilterValues);

      this.movementStats = getNested(response, 'voyageVolumes', []).map(item => ({
        segment: item.cargoStatus,
        volume: item.volume,
      }));
    },

    async loadVesselSegments(apiFilterValues) {
      const response = await this.getVesselSegments(apiFilterValues);

      this.vesselsStats = getNested(response, 'segmentVolumes', []).map(item => ({
        segment: item.subSegment,
        volume: item.volume,
      }));
    },

    async loadVesselsCount(apiFilterValues) {
      let response = await this.getVesselsCount(apiFilterValues);
      this.vesselsCount = getNested(response, 'vesselCount', 0);
    },

    async loadPeriodicStats(apiFilterValues) {
      let targetSectionNames;
      let currentSectionName = apiFilterValues._periodicStatSection;

      if(currentSectionName) {
        let currentSection = this.periodicStat[currentSectionName];
        currentSection.range.value = apiFilterValues._periodicStatRange;
        currentSection.group.value = apiFilterValues._periodicStatGroup;
        targetSectionNames = [currentSectionName];
      } else {
        targetSectionNames = Object.keys(this.periodicStat);
      }

      for(let sectionName of targetSectionNames) {
        const currentSection = this.periodicStat[sectionName];
        const retrieveMethod = this[currentSection.method];
        let parameters = this.getParametersForSection(currentSection);

        currentSection.data = [];
        let response = (await retrieveMethod(parameters)) || {};
        currentSection.data = getNested(response, 'timeseriesChart', []).reverse();
      }
    },

    getParametersForSection(currentSection) {
      return {
        ...this.getBaseFilterParameters(),
        range: currentSection.range.value,
        group: currentSection.group.value,
      };
    },

    getBaseFilterParameters(apiFilterValues = null) {
      apiFilterValues = apiFilterValues ? apiFilterValues : this.apiFilterValues;
      let parameters = {};

      Object.keys(apiFilterValues).forEach(fieldName => {
        if(!fieldName.startsWith('_')) {
          parameters[fieldName] = apiFilterValues[fieldName];
        }
      });
      return parameters;
    },

    handleExportTimeseries(format) {
      console.log(`handleExportTimeseries to: ${format}`);
    },
  },

  computed: {
    directionLoadStats() {
      return {
        region: this.directionStats.regions.load,
        country: this.directionStats.countries.load,
        port: this.directionStats.ports.load,
      };
    },

    directionDischargeStats() {
      return {
        region: this.directionStats.regions.discharge,
        country: this.directionStats.countries.discharge,
        port: this.directionStats.ports.discharge,
      };
    },

    // isFilterReady() {
    //   // uncomment when/if map "boundingBox" is used to filter all api requests
    //   // .applyFilter() should be applied only when filter is ready
    //   const requiredFields = [
    //     'boundingBox',
    //     'segment',
    //     'ladenStatus',
    //     'commodity',
    //     'loadCountry',
    //     'dischargeCountry',
    //   ];
    //
    //   return isEqual(
    //     Object.keys(this.apiFilterValues).sort(),
    //     requiredFields.sort()
    //   );
    // },
  },
}
</script>

<style scoped>
.dashboard-card >>> .card-body {
  /* deep-scoped style, affects child components */
  height: 390px;
}
</style>

<style scoped lang="scss">
.dashboard-map {
  height: 436px;
}
</style>
